import { useMemo } from 'react';
import { Activity, ChevronRight, FileText, ListChecks } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import { EcgStatusBadge } from '@/components/ecg/EcgStatusBadge';
import { ECG_QUALITY_LABELS, type EcgStudyListItem } from '@/lib/ecgStudies';
import {
    filterByRange,
    hasRange,
    rangeInvalid,
    type DateRange,
    type EcgHistory,
} from '@/components/medic/patientCardData';
import { DateRangeFilter } from '@/components/medic/PatientCardDateRange';
import { SectionState } from '@/components/medic/PatientCardStates';

interface Props {
    data: EcgHistory;
    range: DateRange;
    onRangeChange: (next: DateRange) => void;
    onReload: () => void;
    onOpenStudy: (studyId: string) => void;
}

function studyDate(study: EcgStudyListItem): string {
    return formatValue(study.createdAt, 'date:dd.MM.yyyy HH:mm', 'timestamp') || study.title || '—';
}

function hrLabel(study: EcgStudyListItem): string {
    return study.measurements.hrBpm === null ? '—' : String(Math.round(study.measurements.hrBpm));
}

export function PatientCardEcgSection({ data, range, onRangeChange, onReload, onOpenStudy }: Props) {
    const studies = useMemo(
        () => filterByRange(data.studies, (study) => study.createdAt, range),
        [data.studies, range],
    );
    const lastAt = data.studies[0]?.createdAt || '';
    const withConclusion = data.studies.filter((study) => !!study.conclusion.trim()).length;

    if (data.state === 'denied') {
        return <SectionState title="История ЭКГ недоступна" text={data.error || 'Нет прав на чтение исследований.'} tone="warning" />;
    }
    if (data.state === 'unavailable') {
        return <SectionState title="ЭКГ не подключена" text={data.note || 'В этой базе нет исследований ЭКГ.'} />;
    }
    if (data.state === 'error') {
        return <SectionState title="Не удалось загрузить историю ЭКГ" text={data.error} tone="danger" onRetry={onReload} />;
    }

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                <div className="rounded-lg border bg-card px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Исследований</div>
                    <div className="text-lg font-semibold">{data.studies.length}</div>
                </div>
                <div className="rounded-lg border bg-card px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">С заключением</div>
                    <div className="text-lg font-semibold">{withConclusion}</div>
                </div>
                <div className="rounded-lg border bg-card px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Ожидают</div>
                    <div className="text-lg font-semibold">{data.studies.length - withConclusion}</div>
                </div>
                <div className="rounded-lg border bg-card px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Последнее</div>
                    <div className="truncate text-sm font-medium">
                        {lastAt ? formatValue(lastAt, 'date:dd.MM.yyyy HH:mm', 'timestamp') : '—'}
                    </div>
                </div>
            </div>

            {data.note && <SectionState title="Как найти исследования" text={data.note} />}

            <DateRangeFilter value={range} onChange={onRangeChange} />

            {rangeInvalid(range) && (
                <SectionState
                    title="Период задан неверно"
                    text="Дата «с» позже даты «по». Исправьте период или сбросьте фильтр."
                    tone="warning"
                />
            )}

            {!rangeInvalid(range) && studies.length === 0 && (
                <div className="rounded-lg border bg-card px-3 py-4 text-sm text-muted-foreground">
                    <div className="flex items-start gap-2">
                        <Activity className="mt-0.5 h-4 w-4 shrink-0" />
                        <div>
                            {data.studies.length === 0
                                ? 'У пациента пока нет исследований ЭКГ.'
                                : 'За выбранный период исследований ЭКГ нет.'}
                            {data.studies.length > 0 && hasRange(range) && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="ml-2 h-6 px-2 text-[11px]"
                                    onClick={() => onRangeChange({ from: '', to: '' })}
                                >
                                    Сбросить период
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {!rangeInvalid(range) && studies.length > 0 && (
                <div className="overflow-hidden rounded-lg border bg-card">
                    <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
                        <ListChecks className="h-3.5 w-3.5 text-primary" />
                        <span className="text-xs font-medium">История исследований</span>
                        <span className="ml-auto text-[10px] text-muted-foreground">
                            показано {studies.length} из {data.studies.length}
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-xs">
                            <thead>
                                <tr className="border-b text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                                    <th className="px-3 py-1.5 font-medium">Дата</th>
                                    <th className="px-3 py-1.5 font-medium">Статус</th>
                                    <th className="px-3 py-1.5 font-medium">ЧСС</th>
                                    <th className="px-3 py-1.5 font-medium">Качество</th>
                                    <th className="px-3 py-1.5 font-medium">Заключение</th>
                                    <th className="px-3 py-1.5 font-medium text-right"> </th>
                                </tr>
                            </thead>
                            <tbody>
                                {studies.map((study) => (
                                    <tr
                                        key={study.id}
                                        className="cursor-pointer border-b last:border-0 hover:bg-accent/30"
                                        onClick={() => onOpenStudy(study.id)}
                                    >
                                        <td className="whitespace-nowrap px-3 py-2">{studyDate(study)}</td>
                                        <td className="px-3 py-2">
                                            <EcgStatusBadge code={study.statusCode} label={study.statusLabel} />
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">{hrLabel(study)}</td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            {ECG_QUALITY_LABELS[study.qualityVerdict]}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-flex items-center gap-1 ${study.conclusion.trim() ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
                                                <FileText className="h-3 w-3" />
                                                {study.conclusion.trim() ? 'есть' : 'ожидает'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-6 px-2 text-[11px]"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    onOpenStudy(study.id);
                                                }}
                                            >
                                                <ChevronRight className="mr-1 h-3 w-3" />
                                                Открыть
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
